import { MixerHorizontalIcon } from '@radix-ui/react-icons'
import { Button, Flex, Text } from '@weareredlight/design-system'
import { useMemo, useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'

import type {
  ChangeLogParamsType,
  TreatmentProcedureChangeLogType,
} from 'types/changeLog'
import type { TreatmentType } from 'types/treatments'

import TreatmentChangeLogFilters from './TreatmentChangeLogFilters'

import api from 'api/api'
import Table from 'components/Table'
import { useRequest } from 'hooks/useRequest'
import useTableRequestState from 'hooks/useTableRequestState'
import {
  changeLogColumns,
  changeLogDefaultSortees,
} from 'utils/tables/changeLogs'
import { getName } from 'utils/text'

const LogList = () => {
  const [filters, setFilters] = useState<ChangeLogParamsType>()
  const [openFilters, setOpenFilters] = useState(false)
  const { treatmentId, treatmentProcedureId } = useParams<{
    treatmentId: string
    organizationId: string
    treatmentProcedureId: string
  }>()

  const params = useMemo(
    () => ({
      treatmentId: treatmentId || '',
      treatmentProcedureId: treatmentProcedureId || '',
      ...filters,
    }),
    [treatmentId, treatmentProcedureId, filters],
  )

  const { data: treatment, doRequest: getTreatment } = useRequest<
    TreatmentType,
    { id: string }
  >(api.getTreatment)

  const { tableData, ...rest } = useTableRequestState<
    TreatmentProcedureChangeLogType,
    ChangeLogParamsType
  >({
    requestFunc: api.getTreatmentProcedureChangeLogs,
    pageSize: 10,
    params,
    sortees: changeLogDefaultSortees,
  })

  const updateTreatment = useCallback(() => {
    if (!treatmentId) return
    getTreatment({ id: treatmentId })
  }, [getTreatment, treatmentId])

  useEffect(() => {
    updateTreatment()
  }, [updateTreatment])

  return (
    <Flex direction="column" gap="xlg" align="start">
      <Flex gap="xxsm" align="center">
        <Button
          variant="secondary"
          iconComponent={() => <MixerHorizontalIcon />}
          iconPosition="iconOnly"
          onClick={() => {
            setOpenFilters(!openFilters)
          }}
        />
        <Text variant="h3">{getName(treatment?.patient)}</Text>
      </Flex>
      {openFilters && (
        <TreatmentChangeLogFilters getTreatmentChangeLogFilters={setFilters} />
      )}
      <Table
        columns={changeLogColumns()}
        data={tableData?.data || []}
        totalCount={tableData?.count || 0}
        totalPages={tableData?.pageCount || 0}
        {...rest}
      />
    </Flex>
  )
}

export default LogList
